import { useFlag } from '@/services/featureFlags';
import { reaction } from 'mobx';
import { nanoid } from 'nanoid';
import { useEffect } from 'react';
import { useAdvancedEditorContext } from '../context/useAdvancedEditorContext';
import { getAvatarElementFromPage } from '../utils';
import { PolotnoStoreActivePageKeys, getPageProperty, setPageProperty } from './polotno-store';

export const useInheritSceneAvatar = () => {
    const inheritPrevSceneAvatarEnabled = useFlag('inherit_prev_scene_avatar', false);
    const { store, editorInitialized } = useAdvancedEditorContext();

    useEffect(() => {
        if (!inheritPrevSceneAvatarEnabled) return;

        const dispose = reaction(
            () => store.pages?.map(page => page.id) || [],
            async (curr, prev) => {
                if (!editorInitialized || !curr.length || curr.length <= prev.length) {
                    return;
                }

                const newSceneIndex = store.pages.findIndex(page => !prev.includes(page.id));
                const newScene = store.pages[newSceneIndex];

                // @ts-ignore
                const isDuplicate = !!newScene.$treenode._initialSnapshot.custom;
                if (isDuplicate) {
                    return;
                }

                const prevScene = store.pages[newSceneIndex - 1];

                const ttsVoiceConfig = getPageProperty(prevScene, PolotnoStoreActivePageKeys.ttsVoiceConfig);
                const ttsSelectedVoice = getPageProperty(prevScene, PolotnoStoreActivePageKeys.ttsSelectedVoice);
                const selectedPresenterType = getPageProperty(prevScene, PolotnoStoreActivePageKeys.selectedPresenterType);
                const selectedPresenter = getPageProperty(prevScene, PolotnoStoreActivePageKeys.selectedPresenter);
                const selectedDriver = getPageProperty(prevScene, PolotnoStoreActivePageKeys.selectedDriver);
                const selectedEmotion = getPageProperty(prevScene, PolotnoStoreActivePageKeys.selectedEmotion);
                const selectedSentimentId = getPageProperty(prevScene, PolotnoStoreActivePageKeys.selectedSentimentId);
                const ttsIsSelectedDefault = getPageProperty(prevScene, PolotnoStoreActivePageKeys.ttsIsSelectedDefault);
                const avatar = getAvatarElementFromPage(prevScene);

                await store.history.transaction(() => {
                    if (avatar) {
                        const newAvatar = {
                            ...avatar,
                            id: nanoid(),
                            animations: [],
                            filters: [],
                        };
                        newScene.addElement(newAvatar);
                    }

                    setPageProperty(newScene, PolotnoStoreActivePageKeys.ttsVoiceConfig)(ttsVoiceConfig);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.ttsSelectedVoice)(ttsSelectedVoice);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.selectedPresenterType)(selectedPresenterType);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.selectedPresenter)(selectedPresenter);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.selectedDriver)(selectedDriver);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.selectedEmotion)(selectedEmotion);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.selectedSentimentId)(selectedSentimentId);
                    setPageProperty(newScene, PolotnoStoreActivePageKeys.ttsIsSelectedDefault)(ttsIsSelectedDefault);
                });
            }
        );

        return () => {
            dispose();
        };
    }, [inheritPrevSceneAvatarEnabled, editorInitialized]);
};
